import { formatCurrency } from 'common/FormatUtilities';
import { assertDateString, formatDateTimeUrlDate } from 'common/UrlDate';
import { first } from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback } from 'react';
import { RouteChildrenProps } from 'react-router';
import { ToastPosition } from 'react-toastify';
import { getCheckoutQueryString } from 'src/checkout/checkoutUtils';
import { usePersistentDeliveryDates } from 'src/dish-first/usePersistentDeliveryDates';
import {
  PreCheckoutUpsellModal,
  usePreCheckoutUpsellModalContext,
} from 'src/pages/consumer/dish-first/explore/components/PreCheckoutUpsellModal';
import { ConsumerOrderCartDeliveryBanner } from 'src/pages/consumer/order/components/ConsumerOrderCartDeliveryBanner/ConsumerOrderCartDeliveryBanner';
import { useSearchResults } from 'src/pages/consumer/search/useSearchResults';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useLocation } from 'src/shared/hooks/useLocation';
import Notifications from 'src/shared/Notifications';
import { CONSUMER_ORDER_TYPE_QUERY_PARAM_JUST_EDITED, Routes } from 'src/shared/Routes';
import { useIsOnSearchPage } from 'src/shared/utils/SearchUtils';
import { scrollWindowToTop } from 'src/shared/utils/WindowUtils';
import { useDispatch } from 'src/store';
import {
  clearCarts,
  clearQueue,
  setDeliveryDate,
  updateShef,
  userAcknowledgeDeliveryDateChange,
} from 'src/store/cart/actions';
import { ServerAddOnItem } from 'src/store/cart/types';
import { startCheckout } from 'src/store/checkout';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { Spacing } from 'web-common/src/shared/styles';
import { useVisibleAddOnItems } from '../ConsumerOrderCartInfoHooks';
import {
  ConsumerOrderMultiCartInfoModals,
  HandleDeliveryDateChange,
  useMultiOrderCartModal,
} from './ConsumerOrderMultiCartInfoModal';
import {
  Container,
  DeliveryBannerContainer,
  MultiCartTotalsContainer,
  OrderBlocksContainer,
} from './ConsumerOrderMultiCartInfoStyles';
import { MultiCartAddOnItem } from './MultiCartAddOnItem';
import { MultiCartEmpty } from './MultiCartEmpty';
import { MultiCartHeader } from './MultiCartHeader';
import { useMultiCartSelector } from './MultiCartSelector';
import { MultiCartTotals } from './MultiCartTotals';
import { OrderBlock } from './OrderBlock';

const TRIAL_SHEF_MINIMUM_ORDER = 15 * 100; // $15

interface IConsumerOrderCartInfoProps {
  setAddOnQuantity?: (params: { addOnItem: ServerAddOnItem; quantity: number; shefId: string }) => void;
  setActiveShefDeliveryDate: (deliveryDate: DateTime) => void;
  isEditingOrder: boolean;
  headerPercentScrolled: number;
  match: RouteChildrenProps['match'];
}

export const ConsumerOrderMultiCartInfo: React.FC<IConsumerOrderCartInfoProps> = ({
  isEditingOrder,
  setAddOnQuantity,
  setActiveShefDeliveryDate,
  headerPercentScrolled,
  match,
}) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const cart = useMultiCartSelector(isEditingOrder);
  const maybeQueuedAction = first(cart.queuedActions);
  const maybeNewDeliveryDate = maybeQueuedAction ? assertDateString(maybeQueuedAction.payload.deliveryDate) : undefined;
  const { isSmallScreen } = useScreenContext();
  const [, setPersistedDeliveryDates] = usePersistentDeliveryDates();
  const { isOnSearchPage } = useIsOnSearchPage();
  const { refreshSearch } = useSearchResults();

  const handleSetOrderDeliveryDate: HandleDeliveryDateChange = ({ shefIds, deliveryDateTime }) => {
    const prevDeliveryDate = cart.deliveryDate;
    const deliveryDate = formatDateTimeUrlDate(deliveryDateTime);
    dispatch(setDeliveryDate(false, deliveryDate));
    setPersistedDeliveryDates([deliveryDate]);
    shefIds.forEach((shefId) => {
      if (shefId === cart.activeShefId) setActiveShefDeliveryDate(deliveryDateTime);
      dispatch(updateShef({ id: shefId, deliveryDate: formatDateTimeUrlDate(deliveryDateTime) }));
    });
    Notifications.success('Delivery date updated', {
      position:
        isSmallScreen || prevDeliveryDate !== deliveryDate ? ToastPosition.BOTTOM_CENTER : ToastPosition.BOTTOM_RIGHT,
    });
  };

  // cart modals state
  const modal = useMultiOrderCartModal();

  const visibleAddonItems = useVisibleAddOnItems({
    availableAddOnItems: cart.availableAddOnItems,
    addOnItems: cart.addOnItems,
    setAddOnQuantity,
  });

  const canCheckout = cart.checkoutShefIds.length > 0;

  const { show: showUpsellModal } = usePreCheckoutUpsellModalContext();

  const goToCheckout = useCallback(() => {
    if (!canCheckout) return;

    const shefIds = cart.checkoutOrders.map((order) => order.shef.id);
    if (shefIds.length === 0) return;

    dispatch(startCheckout({ shefIds: cart.checkoutShefIds }));
    history.push({
      pathname: Routes.CONSUMER_ORDER_FINALIZE,
      search: getCheckoutQueryString({
        location,
        shefIds,
        urlDate: cart.deliveryDate,
      }),
    });
  }, [canCheckout, cart.checkoutOrders, cart.checkoutShefIds, dispatch, history, location]);

  const onCheckoutClick = useCallback(() => {
    if (!canCheckout) return;

    if (!isEditingOrder) return showUpsellModal();

    const isInvalidTrialShefOrder = cart.hasTrialShef && cart.subtotal < TRIAL_SHEF_MINIMUM_ORDER;

    if (isInvalidTrialShefOrder) {
      const minimum = formatCurrency(TRIAL_SHEF_MINIMUM_ORDER, true);
      Notifications.error(`Orders from the trial kitchen must have a subtotal of at least ${minimum}!`);
    } else if (isEditingOrder) {
      const shefIds = cart.checkoutOrders.map((order) => order.shef.id);
      dispatch(startCheckout({ shefIds: cart.checkoutShefIds }));
      history.push({
        pathname: Routes.CONSUMER_ORDER_FINALIZE,
        search: getCheckoutQueryString({
          location,
          shefIds,
          urlDate: cart.deliveryDate,
          type: CONSUMER_ORDER_TYPE_QUERY_PARAM_JUST_EDITED,
        }),
      });
    } else {
      goToCheckout();
    }
  }, [canCheckout, cart.hasTrialShef, cart.subtotal, goToCheckout, showUpsellModal]);

  const handleAcceptRefreshSearch = useCallback(() => {
    if (isOnSearchPage) {
      refreshSearch();
      setTimeout(scrollWindowToTop, 100);
    }
  }, [isOnSearchPage, refreshSearch]);

  const onEditDeliveryDate = useCallback(
    () => modal.handlers.editMultiOrderDeliveryDate({ shefIds: cart.shefIds }),
    [modal.handlers.editMultiOrderDeliveryDate, cart.shefIds]
  );

  return (
    <>
      <Container>
        <div>
          <MultiCartHeader
            onCheckoutClick={onCheckoutClick}
            deliveryDate={cart.deliveryDateTime}
            onEditDeliveryDate={onEditDeliveryDate}
            headerPercentScrolled={headerPercentScrolled}
            isEditingOrder={isEditingOrder}
            isEmptyCart={cart.isEmpty}
          />
          {!cart.isEmpty ? (
            <OrderBlocksContainer>
              {cart.cartOrders.map(({ shef, lineItems, deliveryDate }) => (
                <OrderBlock
                  key={shef.id}
                  shef={shef}
                  deliveryDate={deliveryDate}
                  lineItems={lineItems}
                  setActiveShefDeliveryDate={setActiveShefDeliveryDate}
                  unavailableLineItemIds={cart.unavailableLineItemIds}
                  availableLineItemCapacities={cart.lineItemAvailabilities}
                  isEditingOrder={isEditingOrder}
                  onEditDeliveryDate={onEditDeliveryDate}
                  match={match}
                />
              ))}
              {visibleAddonItems.map(([addOnItem, quantity]) => (
                <MultiCartAddOnItem
                  key={addOnItem.id}
                  addOnItem={addOnItem}
                  quantity={quantity}
                  paddingX={Spacing.SINGLE}
                  paddingY={Spacing.HALF}
                />
              ))}
              <DeliveryBannerContainer>
                <ConsumerOrderCartDeliveryBanner subtotal={cart.subtotal} />
              </DeliveryBannerContainer>
            </OrderBlocksContainer>
          ) : (
            <MultiCartEmpty />
          )}
        </div>
        {isSmallScreen && !cart.isEmpty && (
          <MultiCartTotalsContainer>
            <MultiCartTotals isEditingOrder={isEditingOrder} floatForward={false} onCheckoutClick={onCheckoutClick} />
          </MultiCartTotalsContainer>
        )}
      </Container>
      <ConsumerOrderMultiCartInfoModals
        modalState={modal.state}
        modalDispatch={modal.dispatch}
        newDeliveryDate={maybeNewDeliveryDate}
        orders={cart.cartOrders}
        onClearCart={() => dispatch(clearCarts())}
        onCancelStartNewOrder={() => dispatch(clearQueue())}
        onAcceptStartNewOrder={modal.handlers.acceptStartNewOrder}
        onAcceptRefreshSearch={handleAcceptRefreshSearch}
        onDeliveryDateChange={handleSetOrderDeliveryDate}
        onAcknowledgeDeliveryDateChange={() => dispatch(userAcknowledgeDeliveryDateChange())}
      />
      <PreCheckoutUpsellModal />
    </>
  );
};
